'use client'

import React, {useState} from "react";
import FormPermuta from "@/components/vendi/FormPermuta";
import InfoCard from "@/components/permuta/InfoCard";
import Image from "next/image";
import StrengthsCard from "@/components/permuta/StrengthsCard";
import PointCard from "@/components/permuta/PointCard";
import {Location} from "@/components/Home/Sedi";
import {Button} from "@/components/ui/button";
import {ArrowRight, Bot, ChevronRight, CircleMinus, CirclePlus} from "lucide-react";
import {RichTextComponents} from "@/components/RichTextComponents";
import {PortableText} from "@portabletext/react";
import CategorySubcategory from "@/components/Faq/FAQCategorySubcategory";
import {openChatbot} from "@/lib/chatbot-events";

function PermutaChatBanner() {
    return (
        <aside
            className="relative overflow-hidden rounded-[1.5rem_0.5rem_1.5rem_0.5rem] border border-primary/15 border-l-4 border-l-primary/70 bg-gradient-to-br from-primary/[0.06] via-white to-blue-50 p-4 shadow-[0_12px_32px_rgba(0,13,125,0.10)] xl:p-5"
            aria-label="Consulente AI per la permuta"
        >
            <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-primary/5" aria-hidden="true" />
            <div className="relative flex items-start gap-4 xl:block">
                <div className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-gradient-custom text-white shadow-md">
                    <Bot className="h-5 w-5" />
                    <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-400" />
                </div>
                <div className="min-w-0 flex-1 xl:mt-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary/70">
                        Consulente AI online
                    </p>
                    <h3 className="mt-1 text-lg font-bold leading-tight text-primary">
                        Hai qualche dubbio?
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                        Fatti consigliare dal nostro consulente AI.
                    </p>
                    <button
                        type="button"
                        onClick={openChatbot}
                        className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-custom xl:mt-4"
                    >
                        Avvia la chat
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </aside>
    );
}


export default function PermutaClient({data, locations, faqs, sectionsVendi}: any) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const toggleFAQ = (index: number) => setOpenIndex(openIndex === index ? null : index);

    return (
        <div className="min-h-screen bg-white">
            <section className="mx-auto max-w-7xl px-4 pb-8 pt-10 md:px-6 md:pb-12 md:pt-16 lg:pt-20">
                <div className="mx-auto max-w-4xl text-center">
                    <h1 className="mb-4 text-3xl font-bold text-primary md:text-4xl lg:text-5xl">{data?.title}</h1>
                    <h2 className="text-lg text-gray-600 md:text-xl lg:text-2xl">{data?.subtitle}</h2>
                </div>

                <div className="relative mx-auto mt-8 max-w-2xl lg:mt-10">
                    <div className="mb-5 xl:hidden">
                        <PermutaChatBanner />
                    </div>
                    <FormPermuta />
                    <div className="absolute left-[calc(100%+1.5rem)] top-5 hidden w-60 xl:block 2xl:left-[calc(100%+2rem)]">
                        <PermutaChatBanner />
                    </div>
                </div>
            </section>

            <section className="mx-auto my-16 flex max-w-7xl flex-col gap-8 px-4 md:my-20 md:px-6">
                {data?.infoCard?.map((card: any) => (
                    <InfoCard
                        key={card._id}
                        number={card.number}
                        title={card.title}
                        description={card.description}
                        imageUrl={card.imageUrl}
                        imageCar={card.imageCar}
                        imagePosition={card.imagePosition}
                    />
                ))}
            </section>

            <section className="mx-auto max-w-7xl px-4 py-4 md:px-6">
                {data?.img1 && (
                    <div className="overflow-hidden rounded-lg">
                        <Image src={data.img1} alt={data?.title1 ?? data?.title ?? ""} width={1000} height={1000} className="w-full object-cover" />
                    </div>
                )}

                <div className="mx-auto mt-6 flex max-w-4xl flex-col gap-6 rounded-lg border border-gray-200 bg-gray-50 p-3 shadow-sm md:p-4">
                    <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-primary px-4 py-5 text-center text-white md:flex-row md:text-2xl">
                        <svg width="34" height="35" viewBox="0 0 34 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M25.8887 12.1667L19.7334 21.3996C19.3075 22.0385 18.3503 21.9789 18.0069 21.2922L15.9926 17.2636C15.6493 16.5768 14.6921 16.5172 14.2662 17.1561L8.1109 26.389" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <rect x="1" y="1.5" width="32" height="32" rx="2" stroke="white" strokeWidth="2"/>
                        </svg>
                        {data?.title1}
                    </div>
                    <p className="px-3 text-center text-lg text-gray-700 md:text-xl">{data?.subtitle1}</p>
                </div>
            </section>

            <section className="mx-auto my-16 max-w-6xl px-4 md:my-20 md:px-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {data?.features?.map((feature: any) => (
                        <StrengthsCard
                            key={feature._id}
                            title={feature.title}
                            description={feature.description}
                            imageUrl={feature.imageUrl}
                            icon={feature.icon}
                        />
                    ))}
                </div>
            </section>

            <section className="mx-auto my-16 max-w-6xl px-4 md:my-20 md:px-6">
                <h4 className="text-center text-3xl font-bold text-primary md:text-4xl">Le nostre sedi</h4>
                <div className="mt-10 grid grid-cols-1 justify-items-center gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {locations?.map((location: Location) => (
                        <PointCard key={location._id} locations={location} />
                    ))}
                </div>
            </section>

            <section className="mx-auto my-16 max-w-7xl px-4 md:my-20 md:px-6">
                <h4 className="text-center text-3xl font-bold text-primary md:text-4xl">I nostri numeri</h4>
                <div className="mx-auto mt-10 rounded-lg bg-primary px-5 py-8 md:px-10">
                    <div className="mx-auto grid max-w-6xl grid-cols-3 gap-6 text-center text-white">
                        <div>
                            <h2 className="text-5xl font-bold md:text-7xl">30+</h2>
                            <p className="text-xl font-medium md:text-3xl">Mila</p>
                            <p className="text-sm opacity-80 md:text-base">di clienti soddisfatti</p>
                        </div>
                        <div>
                            <h2 className="text-5xl font-bold md:text-7xl">45K</h2>
                            <p className="text-xl font-medium md:text-3xl">Auto</p>
                            <p className="text-sm opacity-80 md:text-base">vendute</p>
                        </div>
                        <div>
                            <h2 className="text-5xl font-bold md:text-7xl">6</h2>
                            <p className="text-xl font-medium md:text-3xl">Filiali</p>
                            <p className="text-sm opacity-80 md:text-base">presenti in Italia</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="mx-auto my-16 max-w-7xl px-4 md:my-20 md:px-6">
                <h2 className="px-3 text-center text-2xl leading-snug text-primary md:text-4xl">
                    Vorresti <span className="font-bold">vendere</span> la tua auto ma devi{" "}
                    <span className="font-bold">acquistarne</span> un&apos;altra?
                </h2>
                <p className="mt-2 px-3 text-center text-base text-gray-700 md:text-2xl">
                    Non c&apos;Ã¨ problema, Gruppo Piccirillo vanta un parco auto di{" "}
                    <span className="font-bold">oltre 3.000 veicoli</span>!
                </p>
                {data?.backgroundImage && (
                    <div className="mt-8 overflow-hidden rounded-lg">
                        <Image src={data.backgroundImage} alt={data?.title ?? ""} width={1000} height={1000} className="w-full object-cover" />
                    </div>
                )}
                <div className="mt-10 flex items-center justify-center">
                    <Button className="px-10 text-base text-white" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
                        Vendi la tua auto
                        <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            <section className="mx-auto max-w-3xl px-6 py-10 md:py-16">
                <h2 className="text-center text-4xl font-bold text-gray-900 md:text-5xl">FAQ</h2>
                <p className="mb-6 mt-2 text-center text-gray-600">
                    Tutto quello che devi sapere sulla vendita della tua auto.
                </p>
                <div className="border-t border-gray-300">
                    {faqs?.map((faq: any, index: number) => (
                        <div key={index} className="border-b border-gray-300">
                            <button
                                type="button"
                                className="flex w-full items-center justify-between py-4 text-left font-semibold text-gray-900"
                                onClick={() => toggleFAQ(index)}
                            >
                                {faq.question}
                                {openIndex === index ? <CircleMinus className="h-6 w-6 text-gray-500" /> : <CirclePlus className="h-6 w-6 text-gray-500" />}
                            </button>
                            {openIndex === index && (
                                <div className="pb-4 text-sm leading-relaxed text-gray-700">
                                    <PortableText value={faq.answer} components={RichTextComponents} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {sectionsVendi && sectionsVendi.length > 0 && (
                    <div className="mt-4">
                        {sectionsVendi[0].categories?.map((category: any) => (
                            <CategorySubcategory
                                key={category._id}
                                title={category.title}
                                slug={category.slug}
                                slugSezione={sectionsVendi[0].slug}
                                faqs={category.faqs}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
